import { createContext, forwardRef, useContext, useState } from 'react';
import type { AnchorHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { flavorVar, type Flavor } from './flavors';
import { MenuButton } from './menu-button';
import { WaveStack } from './wave';

const Ctx = createContext<{ close: () => void }>({ close: () => {} });

const DEFAULT_WAVES: Flavor[] = ['grape', 'berry', 'citron'];

interface WaveMenuProps {
  /** Nav links (`WaveMenu.Link` inside `WaveMenu.Nav`) and `WaveMenu.Footer`. */
  children: ReactNode;
  /** Custom trigger; defaults to `<MenuButton>`. */
  trigger?: ReactNode;
  /** Pale panel wash. Default 'lychee'. */
  flavor?: Flavor;
  /** Saturated colours for the stacked waves on the panel's bottom edge. */
  waveFlavors?: Flavor[];
  className?: string;
}

/**
 * Full-width overlay menu that drops from the top of the viewport with a stack
 * of waves along its bottom edge. Built on Radix Dialog (focus trap, scroll
 * lock, Esc, portal). Compose:
 *
 *   <WaveMenu>
 *     <WaveMenu.Nav>
 *       <WaveMenu.Link asChild><Link to="/">Home</Link></WaveMenu.Link>
 *       <WaveMenu.Link href="https://…">GitHub</WaveMenu.Link>
 *     </WaveMenu.Nav>
 *     <WaveMenu.Footer><Button>Get started</Button></WaveMenu.Footer>
 *   </WaveMenu>
 */
function WaveMenuRoot({
  children,
  trigger,
  flavor = 'lychee',
  waveFlavors = DEFAULT_WAVES,
  className,
}: WaveMenuProps) {
  const [open, setOpen] = useState(false);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>
        {trigger ?? <MenuButton open={open} />}
      </DialogPrimitive.Trigger>

      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="wave-menu-overlay fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            'wave-menu-panel fixed inset-x-0 top-0 z-50 max-h-dvh overflow-y-auto',
            'border-b-2 border-ink pb-44 pt-6 focus:outline-none',
            className,
          )}
          style={{ backgroundColor: flavorVar(flavor, 'soft') }}
        >
          <DialogPrimitive.Title className="sr-only">Site menu</DialogPrimitive.Title>
          <div className="mx-auto w-full max-w-5xl px-6 sm:px-8">
            <div className="flex items-center justify-between">
              <span className="font-display text-lg font-semibold text-ink">Menu</span>
              <DialogPrimitive.Close
                aria-label="Close menu"
                className="inline-flex size-10 items-center justify-center rounded-pill border-2 border-ink text-ink transition-transform duration-200 ease-bounce hover:rotate-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
              >
                <X className="size-5" strokeWidth={2.5} />
              </DialogPrimitive.Close>
            </div>

            <Ctx.Provider value={{ close: () => setOpen(false) }}>{children}</Ctx.Provider>
          </div>

          <WaveStack flavors={waveFlavors} position="bottom" height={80} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/* ---------- sub-components ---------- */

interface WaveMenuLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Slot in a router `<Link>` instead of the default `<a>`. */
  asChild?: boolean;
}

const WaveMenuLink = forwardRef<HTMLAnchorElement, WaveMenuLinkProps>(
  ({ asChild = false, className, onClick, children, ...props }, ref) => {
    const { close } = useContext(Ctx);
    const Comp = asChild ? Slot : 'a';
    return (
      <Comp
        ref={ref}
        className={cn(
          'wave-menu-stagger group/link flex items-baseline gap-3 py-2 font-display',
          'text-4xl font-semibold text-ink transition-[transform,letter-spacing] duration-200 ease-bounce',
          'hover:translate-x-1.5 hover:tracking-tight focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink sm:text-5xl',
          className,
        )}
        onClick={(e) => {
          onClick?.(e);
          close();
        }}
        {...props}
      >
        <span
          aria-hidden
          className="text-2xl text-accent opacity-0 transition-opacity duration-200 group-hover/link:opacity-100"
        >
          ›
        </span>
        <Slottable>{children}</Slottable>
      </Comp>
    );
  },
);
WaveMenuLink.displayName = 'WaveMenu.Link';

function WaveMenuNav({ className, ...props }: HTMLAttributes<HTMLElement>) {
  return <nav className={cn('mt-8 flex flex-col', className)} {...props} />;
}

function WaveMenuFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'mt-10 flex flex-wrap items-center gap-4 border-t-2 border-ink/15 pt-8',
        className,
      )}
      {...props}
    />
  );
}

export const WaveMenu = Object.assign(WaveMenuRoot, {
  Link: WaveMenuLink,
  Nav: WaveMenuNav,
  Footer: WaveMenuFooter,
});
